import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL

export const registerFaculty = createAsyncThunk(
  "faculty/registerFaculty",
  async (facultyData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }

      const response = await axios.post(`${API_URL}/auth/faculty/register`, facultyData, config)
      return response.data?.data ?? response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Faculty registration failed")
    }
  }
)

export const fetchFaculty = createAsyncThunk("faculty/fetchFaculty", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/faculty`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data?.data ?? response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchFacultyById = createAsyncThunk("faculty/fetchFacultyById", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/faculty/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data?.data ?? response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchFacultyByEmail = createAsyncThunk(
  "faculty/fetchFacultyByEmail",
  async (email, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_URL}/faculty/email/${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data?.data ?? response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const updateFaculty = createAsyncThunk("faculty/updateFaculty", async ({ id, data }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.put(`${API_URL}/faculty/${id}`, data, {
      headers: { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
    return response.data?.data ?? response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const updateFacultyStatus = createAsyncThunk(
  "faculty/updateFacultyStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.patch(
        `${API_URL}/faculty/${id}/employment-status`,
        { employment_status: status },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      return response.data?.faculty ?? response.data?.data ?? response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const deleteFaculty = createAsyncThunk("faculty/deleteFaculty", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    await axios.delete(`${API_URL}/faculty/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return id
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

const facultySlice = createSlice({
  name: "faculty",
  initialState: {
    faculty: [],
    currentFaculty: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // registerFaculty
      .addCase(registerFaculty.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerFaculty.fulfilled, (state, action) => {
        state.loading = false
        state.faculty.push(action.payload)
      })
      .addCase(registerFaculty.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchFaculty (admin list)
      .addCase(fetchFaculty.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFaculty.fulfilled, (state, action) => {
        state.loading = false
        state.faculty = action.payload
      })
      .addCase(fetchFaculty.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchFacultyById
      .addCase(fetchFacultyById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFacultyById.fulfilled, (state, action) => {
        state.loading = false
        state.currentFaculty = action.payload
        state.error = null
      })
      .addCase(fetchFacultyById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchFacultyByEmail
      .addCase(fetchFacultyByEmail.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFacultyByEmail.fulfilled, (state, action) => {
        state.loading = false
        state.currentFaculty = action.payload
        state.error = null
      })
      .addCase(fetchFacultyByEmail.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // updateFaculty
      .addCase(updateFaculty.fulfilled, (state, action) => {
        const index = state.faculty.findIndex((f) => f.faculty_id === action.payload.faculty_id)
        if (index !== -1) {
          state.faculty[index] = action.payload
        }
        if (state.currentFaculty && state.currentFaculty.faculty_id === action.payload.faculty_id) {
          state.currentFaculty = action.payload
        }
      })
      .addCase(updateFaculty.rejected, (state, action) => {
        state.error = action.payload
      })

      // updateFacultyStatus
      .addCase(updateFacultyStatus.fulfilled, (state, action) => {
        if (!action.payload) {
          console.warn("updateFacultyStatus fulfilled with undefined payload")
          return
        }
        const payload = action.payload
        const id = payload.faculty_id ?? payload.id
        const index = state.faculty.findIndex((f) => f?.faculty_id === id)
        if (index !== -1) {
          state.faculty[index] = payload
        } else {
          console.warn("Faculty not found in state when updating employment status", id)
        }
        if (state.currentFaculty && (state.currentFaculty.faculty_id === id || state.currentFaculty.id === id)) {
          state.currentFaculty = payload
        }
      })
      .addCase(updateFacultyStatus.rejected, (state, action) => {
        state.error = action.payload
      })

      // deleteFaculty
      .addCase(deleteFaculty.fulfilled, (state, action) => {
        state.faculty = state.faculty.filter((f) => f.faculty_id !== action.payload)
      })
      .addCase(deleteFaculty.rejected, (state, action) => {
        state.error = action.payload
      })
  },
})

export const { clearError } = facultySlice.actions
export default facultySlice.reducer